import axios, { AxiosPromise } from 'axios';
import { TextSelectionType } from '../utils/text-selection/textSelection.types';
import { DecisionContextType } from './page/decision.types';
import { SearchQueryParams } from '../watchers/watchers.types';
import {
  DecisionReco,
  DecisionRecoLaw,
  DecisionMoreLikeThisApi,
  DecisionUserHighlight,
  DecisionTextSelection,
} from './decision.types';
import { NavHitsType } from './page/sub-header/decisionSubHeader.types';

export async function fetchDecisionsQuoted(
  docId: string,
  key: string,
): Promise<DecisionReco[]> {
  const res = await axios.get('/getQuotedWith', {
    params: {
      docId,
      key,
    },
  });
  return stringifyAuthorsNumber(res.data.quotedWithDecisions);
}

export async function fetchDecisionsReference(
  docId: string,
  key: string,
): Promise<DecisionReco[]> {
  const res = await axios.get('/getReferenceDecisions', {
    params: {
      docId,
      key,
    },
  });
  return stringifyAuthorsNumber(res.data.refdecisions);
}

export async function fetchDecisionsSimilar(
  docId: string,
  key: string,
): Promise<DecisionReco[]> {
  const res = await axios.get('/getSimilarDecisions', {
    params: {
      docId,
      key,
    },
  });

  return stringifyAuthorsNumber(res.data.simdecisions);
}

export async function getDecisionsLawSimilar(
  docId: string,
  key: string,
): Promise<DecisionRecoLaw[]> {
  const res = await axios.get('/getDecisionsWithSameLaws', {
    params: {
      docId,
      key,
    },
  });
  return stringifyAuthorsNumber(
    res.data.sameLawsDecisions,
  ) as DecisionRecoLaw[];
}

function stringifyAuthorsNumber(array: DecisionRecoLaw[] | DecisionReco[]) {
  const arrayCopy = [...array];
  for (let index = 0; index < arrayCopy.length; index++) {
    const decision = arrayCopy[index];
    const element = decision.numberOfComments;
    let resultString = '';
    if (element > 0) {
      let string = 'auteur a';
      if (element > 1) {
        string = 'auteurs ont';
      }
      resultString = `${element} ${string} commenté cette décision`;
    } else {
      resultString = '';
    }
    decision.numberOfCommentsString = resultString;
  }
  return arrayCopy;
}

export const PDF = {
  generate(html: string, docId: string) {
    const form = document.createElement('form');
    form.setAttribute('action', `/pdf-generator`);
    form.setAttribute('target', '_blank');
    form.setAttribute('method', 'POST');
    form.style.display = 'none';

    const input = document.createElement('input');
    input.name = 'html';
    input.value = html;

    const inputDocId = document.createElement('input');
    inputDocId.name = 'doc_id';
    inputDocId.value = docId;

    form.appendChild(input);
    form.appendChild(inputDocId);

    if (document.body) {
      document.body.appendChild(form);
    }

    form.submit();

    return Promise.resolve();
  },
};

export const getMoreLikeThis = ({
  text,
  docId,
  docKey,
}: {
  text: string;
  docId: string;
  docKey: string;
}): AxiosPromise<DecisionMoreLikeThisApi> =>
  axios.post(`/more_like_this`, {
    like: text,
    docId,
    key: docKey,
  });

export const sendTextSelectionTracking = (
  textSelection: TextSelectionType,
  docId: string,
  eventType: string,
): void => {
  const {
    startWord,
    endWord,
    startWordOccurrence,
    endWordOccurrence,
    selectedString,
    length,
  } = textSelection;
  axios.post('/text_selections', {
    startWord,
    endWord,
    startWordOccurrence,
    endWordOccurrence,
    selectedString,
    length,
    event_type: eventType,
    doc_id: docId,
    version: window.TEXT_SELECTIONS_CURRENT_VERSION,
    selected_string:
      textSelection.selectedString.length <= 500
        ? textSelection.selectedString
        : null,
  });
};

export const getDecisionUserHighlights = async (
  docId: string,
  documentReadKey: string,
): Promise<DecisionUserHighlight[]> => {
  return (
    await axios.get('/getDecisionUserHighlights', {
      params: {
        docId,
        documentReadKey,
      },
    })
  ).data;
};

export const getDecisionTextSelections = async (
  docId: string,
): Promise<DecisionTextSelection[]> => {
  return (
    await axios.get('/getDecisionTextSelections', {
      params: {
        docId,
      },
    })
  ).data;
};

export async function fetchNavHits(
  queryParams: SearchQueryParams,
  docId: DecisionContextType['docId'],
): Promise<NavHitsType> {
  return (
    await axios.get('/getNavHits', {
      params: {
        queryParams,
        docId,
      },
    })
  ).data;
}
